import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
  IonHeader,
  IonToolbar,
  IonButton,
  IonButtons,
  IonIcon,
  IonTabs,
  IonTabBar,
  IonTabButton,
  IonBadge,
  IonText,
  IonImg,
  IonLabel,
  NavController,
  IonSkeletonText,
  MenuController
} from '@ionic/angular/standalone';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { CharityService } from 'src/app/core/services/charity/charity.service';
import { CharityManagementService } from 'src/app/core/services/charity/charity-management.service';
import { CharityRole } from 'src/app/core/models/CharityUser';
import { distinctUntilChanged, filter, Subscription } from 'rxjs';
import { NotificationsService } from 'src/app/core/services/notifications/notifications.service';
import { TabService } from 'src/app/core/services/navigation/tab.service';
import { StateType } from 'src/app/core/models/StateType';
import { AuthService } from 'src/app/core/services/auth/auth.service';
import { Charity } from 'src/app/core/models/Charity';
import { AlertController } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { chevronBackOutline, notifications, notificationsOutline, chevronForwardOutline, calendar, calendarOutline, swapHorizontalOutline, logOutOutline, closeOutline } from 'ionicons/icons';
import { environment } from 'src/environments/environment';
import { isDesktopViewport } from 'src/app/core/utilities/viewport';
import { BannerDismissalService } from 'src/app/core/services/banner/banner-dismissal.service';
import { BannerLayoutService } from 'src/app/core/services/banner/banner-layout.service';
import { BannerHeightDirective } from 'src/app/shared/directives/banner-height.directive';

const CHARITY_TABS: string[] = [
  '/charity/charity-info',
  '/charity/newsfeed',
  '/charity/events',
  '/charity/commerce',
];

const charityTabs: string[] = ['charity-info', 'newsfeed', 'events', 'commerce'];

@Component({
    selector: 'app-charity-role-tabs',
    templateUrl: './charity-role-tabs.component.html',
    styleUrls: ['./charity-role-tabs.component.scss'],
    imports: [
        IonHeader,
        IonToolbar,
        IonButton,
        IonButtons,
        IonIcon,
        IonTabs,
        IonTabBar,
        IonTabButton,
        IonBadge,
        IonText,
        IonLabel,
        IonImg,
        IonSkeletonText,
        BannerHeightDirective
    ]
})
export class CharityRoleTabsComponent implements OnInit, OnDestroy {
  private readonly subscriptions = new Subscription();
  private menuController = inject(MenuController);
  private readonly bannerDismissalService = inject(BannerDismissalService);
  private readonly bannerLayoutService = inject(BannerLayoutService);
  authService = inject(AuthService);
  appName = environment.appName;

  currentTab!: string;
  /** Sidebar highlight: derived from the current URL so it follows back/forward and deep links. */
  activeSection: string = '';
  isMainPage: boolean = true;
  isSpecialPage: boolean = false;
  notificationCount: number = 0;
  private isNavigating: boolean = false;
  winningBanner: boolean = false;
  charity!: Charity;
  imageLoaded: boolean = false;
  pageTitle: string = '';
  currentUserRole: CharityRole = 'MEMBER';
  isStripeBannerDismissed: boolean = false;
  /** The Stripe Account page is where the banner sends people; showing it there too is noise. */
  isOnStripeAccountPage: boolean = false;

  get pageTitleClass(): string {
    return this.pageTitle && this.pageTitle.length > 14 ? 'page-title page-title-small' : 'page-title';
  }

  get canManageDonations(): boolean {
    return this.currentUserRole === 'OWNER' || this.currentUserRole === 'ADMIN';
  }

  get showStripeActivationBanner(): boolean {
    return !!this.charity && !this.charity.stripeOnboardingComplete && !this.isStripeBannerDismissed && !this.isOnStripeAccountPage;
  }

  constructor(
    private navController: NavController,
    private router: Router,
    private charityService: CharityService,
    private charityManagementService: CharityManagementService,
    private notificationsService: NotificationsService,
    private tabService: TabService,
    private route: ActivatedRoute,
    private alertController: AlertController
  ) {
    addIcons({ chevronBackOutline, notifications, notificationsOutline, chevronForwardOutline, calendar, calendarOutline, swapHorizontalOutline, logOutOutline, closeOutline });

    const currentUrl: string = this.router.url;
    if (currentUrl.startsWith('/charity/')) {
      const tabName: string | undefined = currentUrl.split('/')[2]?.split('?')[0];
      if (tabName && charityTabs.includes(tabName)) {
        this.currentTab = tabName;
        this.tabService.setLastTab('charity', tabName);
      }
    }

    if (!this.currentTab) this.currentTab = this.tabService.getLastTab('charity');
    this.activeSection = this.tabService.findTabForUrl('charity', this.router.url, this.currentTab);

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        const url: string = event.urlAfterRedirects;
        if (!url.startsWith('/charity')) return;
        this.isMainPage = !!CHARITY_TABS.find(page => page === url);
        this.isOnStripeAccountPage = url.startsWith('/charity/stripe-account');
        this.updateBannerVisibility();
        
        const state: StateType = this.router.getCurrentNavigation()?.extras.state || {};
        const sourceTab: string | undefined = state['sourceTab'];
        
        if (this.isMainPage) {
          const tabName: string | undefined = url.split('/').pop();
          if (tabName && charityTabs.includes(tabName)) {
            this.currentTab = tabName;
            this.tabService.setLastTab('charity', tabName);
          }
        } else if (sourceTab) {
          this.currentTab = sourceTab;
          this.tabService.setLastTab('charity', sourceTab);
        }
        
        this.activeSection = this.tabService.findTabForUrl('charity', url, this.currentTab);

        let currentRoute: ActivatedRoute = this.route.root;
        while (currentRoute.firstChild) {
          currentRoute = currentRoute.firstChild;
        }
        this.pageTitle = currentRoute.snapshot.data['title'] || '';
        this.tabService.trackNavigation(
          'charity',
          this.currentTab,
          event.urlAfterRedirects,
          state,
        );
      }
    });
  }

  ngOnInit() {
    this.subscriptions.add(
      this.charityService.charity$.pipe(
        filter(charity => !!charity?.id),
        distinctUntilChanged((prev, curr) => 
          prev?.id === curr?.id && prev?.stripeOnboardingComplete === curr?.stripeOnboardingComplete
        )
      ).subscribe(charity => {
        this.charity = charity!;
        if (charity) {
          this.isStripeBannerDismissed = this.bannerDismissalService.isDismissed(
            'stripeActivation',
            charity.id ?? '',
          );
          this.getNotificationCount(charity.id ?? '');
          this.loadCurrentUserRole();
          if (!charity?.avatarUrl) {
            this.charity.avatarUrl = 'assets/ic-charity.svg';
          }
          this.updateBannerVisibility();
        }
      }),
    );
  }

  private updateBannerVisibility(): void {
    this.bannerLayoutService.setVisible('charity', this.showStripeActivationBanner);
  }

  private loadCurrentUserRole() {
    this.subscriptions.add(
      this.charityManagementService.charityUser$.subscribe({
        next: (charityUser) => {
          this.currentUserRole = charityUser?.role || 'MEMBER';
        }
      })
    );
  }

  getNotificationCount(id: string) {
    this.notificationsService.loadUnreadCountByCharityId(Number(id));
    this.subscriptions.add(
      this.notificationsService.charityNotificationCount$.subscribe(count => {
        this.notificationCount = count ?? 0;
      }),
    );
  }

  /**
   * Snoozes the Stripe activation banner for a day. Stops propagation so the
   * dismiss control doesn't also trigger the banner's navigate-to-setup click.
   */
  dismissStripeBanner(event: Event): void {
    event.stopPropagation();
    this.bannerDismissalService.dismiss('stripeActivation', this.charity?.id ?? '');
    this.isStripeBannerDismissed = true;
    this.updateBannerVisibility();
  }

  goToStripeAccount() {
    if (this.canManageDonations) {
      this.navController.navigateForward('/charity/stripe-account');
    } else {
      this.alertController.create({
        header: 'Access Denied',
        message: 'You do not have permission to manage Stripe settings. Only OWNER and ADMIN roles can access this feature.',
        buttons: ['OK']
      }).then(alert => alert.present());
    }
  }

  navigateToTab(tab: string) {
    if (this.isNavigating) return;

    this.isNavigating = true;

    if (isDesktopViewport()) {
      // Desktop sidebar: always open the section root and drop that tab's stack. Restoring the
      // last deep page (the mobile tab-bar behavior below) made sidebar links feel broken.
      this.currentTab = tab;
      this.activeSection = tab;
      this.tabService.setLastTab('charity', tab);
      this.tabService.resetTabHistory('charity', tab);
      this.navController.navigateRoot(`/charity/${tab}`)
        .finally(() => {
          this.isNavigating = false;
        });
      return;
    }

    if (tab === this.currentTab) {
      this.tabService.resetTabHistory('charity', tab);
      this.navController.navigateRoot(`/charity/${tab}`)
        .finally(() => {
          this.isNavigating = false;
        });
    } else {
      this.currentTab = tab;
      this.tabService.setLastTab('charity', tab);
      const lastTabPage: { url: string; state: StateType } | null = this.tabService.getLastTabPage(
        'charity',
        tab,
      );
      if (lastTabPage) {
        this.navController.navigateRoot(lastTabPage.url, { state: lastTabPage.state || {} })
          .finally(() => {
            this.isNavigating = false;
          });
      } else {
        this.navController.navigateRoot(`/charity/${tab}`)
          .finally(() => {
            this.isNavigating = false;
          });
      }
    }
  }

  /**
   * Bookkeeping only — `ion-tabs` has already started the navigation (it restores the tab's last
   * page or root itself). Navigating here too re-ran the same URL with `onSameUrlNavigation:
   * 'reload'`, rebuilding the page on every tab tap. See UserRoleTabsComponent.handleTabChange.
   */
  handleTabChange(event: { tab: string }): void {
    const page: string = event.tab;
    if (!charityTabs.includes(page)) {
      if (!this.currentTab) this.currentTab = this.tabService.getLastTab('charity');
      return;
    }
    if (page !== this.currentTab) {
      this.currentTab = page;
      this.tabService.setLastTab('charity', page);
    }
  }

  goToMenuPage() {
    //this.navController.navigateForward(['/charity-role-menu']).then(r => r);
    this.menuController.open('charity-menu');
  }

  goToNotificationsPage() {
    this.navController.navigateForward(['/charity/charity-notifications']).then(r => r);
  }

  goToWinningItems() {
    this.navController.navigateRoot(`/main/winning-items`).then(() => {
      this.isNavigating = false;
    });
  }

  back() {
    if (!this.currentTab) this.currentTab = this.tabService.getLastTab('charity');
    const backTabPage: { url: string; state: StateType } | null = this.tabService.navigateBack(
      'charity',
      this.currentTab,
    );
    if (backTabPage) {
      this.navController.navigateBack(backTabPage.url, { state: backTabPage.state || {} });
    } else {
      this.navController.navigateRoot(`/charity/${this.currentTab}`);
    }
  }

  sidebarSwitchToUser(): void {
    const lastPage: { url: string; state: StateType } | null = this.tabService.getLastPage('user');
    this.navController.navigateRoot(lastPage?.url || '/main/home', {
      state: lastPage?.state || {},
    });
  }

  async sidebarSignOut(): Promise<void> {
    await this.authService.logout();
    this.navController.navigateRoot('/welcome');
  }

  ngOnDestroy(): void {
    this.bannerLayoutService.setVisible('charity', false);
    this.subscriptions.unsubscribe();
  }
}
